import React, { useState } from "react"
import PropTypes from "prop-types"
import styled from "@emotion/styled"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import Stack from "@mui/material/Stack"
import Box from "@mui/material/Box"
import { useSnackbar } from "notistack"
import { Controller, useForm } from "react-hook-form"
import { addCategory, updateCategory } from "services/category"

const StyledCategoryDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(560px, calc(100vw - 30px));
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 22px 48px rgba(15, 23, 42, 0.18);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 12px;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .MuiDialogContent-root {
    padding: 6px 22px 12px;
  }

  .MuiDialogActions-root {
    padding: 10px 22px 20px;
    gap: 10px;
  }

  .MuiFormControlLabel-label {
    font-size: 13px;
    color: var(--cms-text-primary);
    font-weight: 600;
  }

  .MuiInputBase-root {
    border-radius: 10px;
    background-color: var(--cms-input-bg);
  }

  .dialog-cancel-btn {
    text-transform: none;
    font-weight: 700;
    color: var(--cms-text-secondary);
  }

  .dialog-submit-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(29, 78, 216, 0.22);
  }
`

const defaultCategoryValues = {
  name: "",
  fullName: "",
  iconUrl: "",
  isOnboarding: true,
  pubStatus: true,
}

const CategoryDlg = ({ onSubmit, openMediaManager }) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: defaultCategoryValues,
  })
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [categoryId, setCategoryId] = useState(null)

  const { enqueueSnackbar } = useSnackbar()

  const getSubmitErrorMessage = (error, fallbackMessage) => {
    const serverMessage = error?.response?.data?.message
    if (Array.isArray(serverMessage)) {
      return serverMessage.join(", ")
    }
    return serverMessage || error?.message || fallbackMessage
  }

  const handleClose = () => {
    setOpen(false)
  }

  const openDlg = (editCategory) => {
    const hasEditTarget = Boolean(editCategory)
    setIsEditMode(hasEditTarget)
    setCategoryId(hasEditTarget ? editCategory.id : null)
    reset(
      hasEditTarget
        ? {
            name: editCategory.name || "",
            fullName: editCategory.fullName || "",
            iconUrl: editCategory.iconUrl || "",
            isOnboarding: editCategory.isOnboarding === 1,
            pubStatus: editCategory.pubStatus === 1,
          }
        : defaultCategoryValues
    )
    setOpen(true)
  }

  const iconUrl = watch("iconUrl")

  const handleSelectIcon = (media) => {
    const selectedMedia = Array.isArray(media) ? media[0] : media
    const selectedUrl =
      selectedMedia?.urlDto?.imgUrl ||
      selectedMedia?.urlDto?.originalUrl ||
      selectedMedia?.url ||
      ""

    if (!selectedUrl) {
      enqueueSnackbar("The selected image does not have a usable URL", {
        variant: "warning",
      })
      return
    }

    setValue("iconUrl", selectedUrl, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleAction = async (data) => {
    const fallbackErrorMessage = isEditMode
      ? "Update category fail"
      : "Create category fail"

    setLoading(true)
    try {
      const response = isEditMode
        ? await updateCategory({ ...data, id: categoryId })
        : await addCategory(data)

      if (response.data.rtnCode === 200) {
        enqueueSnackbar(
          isEditMode ? "Update category success" : "Create category success",
          { variant: "success" }
        )
        onSubmit()
        handleClose()
        return
      }

      enqueueSnackbar(response?.data?.message || fallbackErrorMessage, {
        variant: "warning",
      })
    } catch (error) {
      console.error("Category submit error:", error)
      enqueueSnackbar(getSubmitErrorMessage(error, fallbackErrorMessage), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const dlg = (
    <StyledCategoryDialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          {isEditMode ? "Edit Category" : "Add Category"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              autoComplete="off"
              id="cat-name"
              label="Category Name"
              type="text"
              fullWidth
              {...register("name", { required: true })}
              error={!!errors?.name}
              helperText={!!errors?.name ? "Category name is required" : " "}
              variant="outlined"
            />

            <TextField
              autoComplete="off"
              id="cat-full-name"
              label="Full Name"
              type="text"
              fullWidth
              {...register("fullName", { required: true })}
              error={!!errors?.fullName}
              helperText={!!errors?.fullName ? "Full name is required" : " "}
              variant="outlined"
            />
            <Box>
              <TextField
                autoComplete="off"
                id="cat-icon-url"
                label="Category Icon URL"
                type="url"
                fullWidth
                {...register("iconUrl")}
                helperText="Choose an image from Media Manager. Leave empty to use the existing built-in icon."
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <Button
                      type="button"
                      size="small"
                      onClick={() => openMediaManager(handleSelectIcon, "image", "search")}
                    >
                      Browse
                    </Button>
                  ),
                }}
              />
              {iconUrl && (
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1.5,
                    mt: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={iconUrl}
                    alt="Category icon preview"
                    sx={{
                      backgroundColor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                      height: 56,
                      objectFit: "contain",
                      p: 1,
                      width: 56,
                    }}
                  />
                  <Button
                    type="button"
                    size="small"
                    color="error"
                    onClick={() =>
                      setValue("iconUrl", "", {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  >
                    Use default icon
                  </Button>
                </Box>
              )}
            </Box>
            <Controller
              name="pubStatus"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(event) => {
                        field.onChange(event.target.checked)
                      }}
                      color="primary"
                    />
                  }
                  label="Visible in app"
                />
              )}
            />
            <Controller
              name="isOnboarding"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(event) => {
                        field.onChange(event.target.checked)
                      }}
                      color="primary"
                    />
                  }
                  label="Show Onboarding"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button className="dialog-cancel-btn" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            className="dialog-submit-btn"
            disabled={loading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </StyledCategoryDialog>
  )

  return [openDlg, dlg, loading]
}

CategoryDlg.propTypes = {
  onSubmit: PropTypes.func,
  openMediaManager: PropTypes.func.isRequired,
}

CategoryDlg.defaultProps = {
  onSubmit: null,
}

export default CategoryDlg
